import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { customEvent } from "../../util/event.ts";
import * as Styles from "../../styles.ts";
import "../dui/icon/dui-icon.ts";

export const activateCanvasViewEvent = customEvent<string>(
  "activate-canvas-view",
  {
    bubbles: true,
    composed: true,
  },
);

const styles = css`
  :host {
    display: block;
    width: 100%;
    container-type: inline-size;
  }

  .card {
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    overflow: hidden;
    display: flex;
    flex-direction: row;
    gap: var(--space-4);
    width: 100cqw;
    padding: var(--space-2) var(--space-2) var(--space-2) var(--space-4);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-snug);
    cursor: pointer;
    align-items: center;
  }

  .card:hover {
    background: var(--muted);
  }

  .card-actions {
    margin-left: auto;
  }
`;

/**
 * A clickable card component that launches a canvas view.
 *
 * Displays the view name, and dispatches an activation event when clicked.
 *
 * @element canvas-view-card
 *
 * @prop {string} view - The unique identifier for the canvas view
 * @prop {string} name - The display name of the view (defaults to "View")
 *
 * @fires activate-canvas-view - Dispatched when the card is clicked, containing the view ID
 *
 * @example
 * ```html
 * <canvas-view-card
 *   view="main-view-123"
 *   name="Main View">
 * </canvas-view-card>
 * ```
 */
@customElement("canvas-view-card")
export class UiCanvasViewCard extends LitElement {
  @property()
  accessor view: string = "";

  @property()
  accessor name: string = "View";

  static override styles = [Styles.base, styles];

  #onClick = (): void => {
    this.dispatchEvent(activateCanvasViewEvent(this.view));
  };

  override render(): TemplateResult {
    return html`
      <div class="card" @click="${this.#onClick}">
        <span class="card-name">${this.name}</span>
        <div class="card-actions">
          <dui-icon icon="open_in_full" size="var(--space-4)"></dui-icon>
        </div>
      </div>
    `;
  }
}
